import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  FileText, CheckSquare, Package, ArrowRight, ChevronRight, 
  MapPin, Clock, AlertTriangle, CheckCircle, XCircle, TrendingUp,
  Globe, Truck, Shield, BarChart3, Users, Sparkles
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EximStack = () => {
  const [demoData, setDemoData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDemoData();
  }, []);

  const fetchDemoData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/eximstack/demo-summary`);
      const data = await response.json();
      setDemoData(data);
    } catch (error) {
      console.error('Error fetching demo data:', error);
    } finally {
      setLoading(false);
    }
  };

  const seedDemoData = async () => {
    setLoading(true);
    try {
      await fetch(`${API_URL}/api/eximstack/seed-demo-data`, { method: 'POST' });
      await fetchDemoData();
    } catch (error) {
      console.error('Error seeding demo data:', error);
    }
    setLoading(false);
  };

  const modules = [
    {
      id: 'traceability',
      title: 'Traceability Dossier',
      subtitle: 'Farm to Fork Tracking',
      description: 'Complete origin documentation from farm source to final destination. Track every step of your product journey with verifiable evidence.',
      icon: FileText,
      color: '#4CAF50',
      bgColor: 'bg-green-50',
      link: '/eximstack/traceability',
      features: ['Farm source details', 'Processing records', 'Transport logs', 'Certification tracking']
    },
    {
      id: 'qa-gates',
      title: 'QA Gates',
      subtitle: 'Stage-wise Quality Checkpoints',
      description: 'Systematic quality inspection at 6 critical stages. Ensure every batch meets your specifications before moving forward.',
      icon: CheckSquare,
      color: '#2196F3',
      bgColor: 'bg-blue-50',
      link: '/eximstack/qa-gates',
      features: ['6-stage inspection', 'Checklist verification', 'Photo evidence', 'Digital sign-off']
    },
    {
      id: 'export-pack',
      title: 'Export Pack',
      subtitle: 'Complete Documentation',
      description: 'All export documents in one place. Track status, verify completeness, and ensure compliance for smooth customs clearance.',
      icon: Package,
      color: '#F47D20',
      bgColor: 'bg-orange-50',
      link: '/eximstack/export-pack',
      features: ['15+ document types', 'Status tracking', 'Compliance check', 'Digital repository']
    }
  ];

  const stats = [
    { label: 'Active Shipments', value: demoData?.dossiers?.count || 0, icon: Truck, color: 'text-green-600' },
    { label: 'QA Records', value: demoData?.qa_records?.count || 0, icon: CheckSquare, color: 'text-blue-600' },
    { label: 'Export Packs', value: demoData?.export_packs?.count || 0, icon: Package, color: 'text-orange-600' },
    { label: 'Countries', value: 4, icon: Globe, color: 'text-purple-600' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-blue-50">
      {/* Hero Section */}
      <section className="relative py-20 px-4 bg-gradient-to-r from-[#263776] via-[#1e2d5f] to-[#263776] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-64 h-64 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-[#F47D20] rounded-full blur-3xl"></div>
        </div>
        <div className="relative max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            <Badge className="bg-[#F47D20] text-white mb-4">
              <Sparkles className="w-3 h-3 mr-1" /> Shipment Operating System
            </Badge>
            <h1 className="text-4xl md:text-6xl font-bold mb-4">
              EximStack
            </h1>
            <p className="text-xl md:text-2xl text-[#F47D20] font-medium mb-4">
              Traceability + QA + Export Documentation
            </p>
            <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
              Operationalized for real shipments. A shipment-level operating system that keeps every order buyer-ready with complete transparency and compliance.
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <Badge variant="outline" className="bg-white/10 text-white border-white/30 px-4 py-2">
                <MapPin className="w-4 h-4 mr-2" /> Origin Tracking
              </Badge>
              <Badge variant="outline" className="bg-white/10 text-white border-white/30 px-4 py-2">
                <Shield className="w-4 h-4 mr-2" /> QA Verification
              </Badge>
              <Badge variant="outline" className="bg-white/10 text-white border-white/30 px-4 py-2">
                <FileText className="w-4 h-4 mr-2" /> Doc Management
              </Badge>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-8 px-4 bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, idx) => (
              <div key={idx} className="flex items-center gap-4">
                <div className={`p-3 rounded-xl bg-gray-50`}>
                  <stat.icon className={`w-6 h-6 ${stat.color}`} />
                </div>
                <div>
                  <div className="text-2xl font-bold text-[#263776]">{stat.value}</div>
                  <div className="text-sm text-gray-500">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What It Solves */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#263776] mb-3">What It Solves</h2>
            <p className="text-gray-600">Critical pain points in international commodity trade</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-l-4 border-l-red-500 hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <AlertTriangle className="w-10 h-10 text-red-500 mb-4" />
                <h3 className="text-xl font-semibold text-[#263776] mb-2">Spec Drift</h3>
                <p className="text-gray-600">
                  Inconsistent specifications across lots lead to disputes, rejections, and costly re-negotiations with buyers.
                </p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-amber-500 hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <XCircle className="w-10 h-10 text-amber-500 mb-4" />
                <h3 className="text-xl font-semibold text-[#263776] mb-2">Missing QA Evidence</h3>
                <p className="text-gray-600">
                  Lack of documented quality checks creates trust gaps with buyers and increases inspection costs at destination.
                </p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-purple-500 hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <Clock className="w-10 h-10 text-purple-500 mb-4" />
                <h3 className="text-xl font-semibold text-[#263776] mb-2">Documentation Gaps</h3>
                <p className="text-gray-600">
                  Incomplete or incorrect paperwork causes customs delays, demurrage charges, and shipment holds.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Three Core Modules */}
      <section className="py-16 px-4 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#263776] mb-3">Three Core Modules</h2>
            <p className="text-gray-600">Integrated solutions for complete shipment management</p>
          </div>
          <div className="grid lg:grid-cols-3 gap-8">
            {modules.map((module) => (
              <Link key={module.id} to={module.link}>
                <Card className="group h-full hover:shadow-2xl transition-all duration-300 border-0 overflow-hidden">
                  <div 
                    className={`${module.bgColor} p-6 border-b`}
                    style={{ borderBottomColor: module.color }}
                  >
                    <div 
                      className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4"
                      style={{ backgroundColor: module.color }}
                    >
                      <module.icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-[#263776] mb-1">{module.title}</h3>
                    <p className="text-sm font-medium" style={{ color: module.color }}>{module.subtitle}</p>
                  </div>
                  <CardContent className="p-6">
                    <p className="text-gray-600 mb-6">{module.description}</p>
                    <ul className="space-y-2 mb-6">
                      {module.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                          <CheckCircle className="w-4 h-4" style={{ color: module.color }} />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <div className="flex items-center text-[#F47D20] font-medium group-hover:translate-x-2 transition-transform">
                      Explore Module <ArrowRight className="w-4 h-4 ml-2" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Demo Shipments */}
      {demoData && demoData.dossiers?.items?.length > 0 && (
        <section className="py-16 px-4 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-3xl font-bold text-[#263776] mb-2">Live Shipment Tracking</h2>
                <p className="text-gray-600">View real-time status of active shipments</p>
              </div>
              <Button onClick={seedDemoData} variant="outline" disabled={loading}>
                Refresh Demo Data
              </Button>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {demoData.dossiers.items.map((dossier, idx) => {
                const qaRecord = demoData.qa_records?.items?.find(q => q.dossier_id === dossier.id);
                const exportPack = demoData.export_packs?.items?.find(e => e.dossier_id === dossier.id);
                
                return (
                  <Card key={idx} className="hover:shadow-lg transition-shadow">
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-lg text-[#263776]">{dossier.product_name}</CardTitle>
                          <p className="text-sm text-gray-500">{dossier.batch_id}</p>
                        </div>
                        <Badge 
                          className={
                            dossier.status === 'Shipped' ? 'bg-green-100 text-green-800' :
                            dossier.status === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }
                        >
                          {dossier.status}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                        <MapPin className="w-4 h-4" />
                        <span>{dossier.origin_country}</span>
                        <ArrowRight className="w-4 h-4" />
                        <span>{dossier.destination_country}</span>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-4">
                        <Link to={`/eximstack/traceability/${dossier.id}`} className="text-center p-3 bg-green-50 rounded-lg hover:bg-green-100 transition-colors">
                          <FileText className="w-5 h-5 mx-auto mb-1 text-green-600" />
                          <div className="text-xs font-medium text-green-800">Traceability</div>
                          <div className="text-xs text-green-600">View</div>
                        </Link>
                        <Link to={`/eximstack/qa-gates/${qaRecord?.id || ''}`} className="text-center p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
                          <CheckSquare className="w-5 h-5 mx-auto mb-1 text-blue-600" />
                          <div className="text-xs font-medium text-blue-800">QA Gates</div>
                          <div className="text-xs text-blue-600">{qaRecord?.overall_status || 'Pending'}</div>
                        </Link>
                        <Link to={`/eximstack/export-pack/${exportPack?.id || ''}`} className="text-center p-3 bg-orange-50 rounded-lg hover:bg-orange-100 transition-colors">
                          <Package className="w-5 h-5 mx-auto mb-1 text-orange-600" />
                          <div className="text-xs font-medium text-orange-800">Export Pack</div>
                          <div className="text-xs text-orange-600">{exportPack?.completion_percentage || 0}%</div>
                        </Link>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* How It Works */}
      <section className="py-16 px-4 bg-[#263776] text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-3">How It Works</h2>
            <p className="text-white/70">Five simple steps to shipment excellence</p>
          </div>
          <div className="grid md:grid-cols-5 gap-6">
            {[
              { step: 1, title: 'Create Dossier', desc: 'Register batch with farm & harvest details' },
              { step: 2, title: 'Run QA Gates', desc: 'Complete checklist at each processing stage' },
              { step: 3, title: 'Compile Docs', desc: 'Upload and verify export documents' },
              { step: 4, title: 'Generate Pack', desc: 'Create complete shipment documentation' },
              { step: 5, title: 'Track & Share', desc: 'Monitor status and share with buyers' }
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="w-12 h-12 rounded-full bg-[#F47D20] text-white font-bold text-xl flex items-center justify-center mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-white/70">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 bg-gradient-to-r from-[#F47D20] to-orange-500 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Streamline Your Exports?</h2>
          <p className="text-white/90 mb-8">
            Get started with EximStack and bring transparency to every shipment.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-[#F47D20] hover:bg-gray-100">
                Request a Demo
              </Button>
            </Link>
            <Link to="/eximstack/traceability/new">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                Create Your First Dossier
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EximStack;
